import React from "react";
import { motion } from "framer-motion";
import { CheckCircle2, XCircle, AlertTriangle, Shield, Bot, Eye, AlertOctagon } from "lucide-react";

const RiskComplianceReview = ({ riskChecks, onRiskCheckChange }) => {
  const checks = [
    {
      id: "no_spam",
      title: "No Spammy Behaviour",
      description: "No evidence of excessive posting, mass messaging, or spam tactics",
      icon: AlertOctagon,
      color: "red",
      critical: true,
    },
    {
      id: "no_fake_followers",
      title: "No Fake Followers/Bot Activity",
      description: "Audience appears genuine with real engagement patterns",
      icon: Bot,
      color: "orange",
      critical: true,
    },
    {
      id: "no_misleading_content",
      title: "No Misleading/Deceptive Content",
      description: "Content is honest, transparent, and not misleading",
      icon: Eye,
      color: "yellow",
      critical: true,
    },
    {
      id: "no_previous_abuse",
      title: "No Previous Affiliate Abuse",
      description: "No known history of affiliate program violations",
      icon: Shield,
      color: "purple",
      critical: true,
    },
  ];

  const allPassed = checks.every((check) => riskChecks[check.id] === true);
  const anyFailed = checks.some((check) => riskChecks[check.id] === false);
  const allChecked = checks.every((check) => riskChecks[check.id] !== null && riskChecks[check.id] !== undefined);

  const checkedCount = checks.filter((check) => riskChecks[check.id] !== null && riskChecks[check.id] !== undefined).length;
  const progressPercentage = (checkedCount / checks.length) * 100;

  return (
    <div className="p-6 bg-white rounded-xl border border-gray-200 shadow-sm">
      <div className="pb-4 mb-6 border-b border-gray-100">
        <div className="flex gap-3 justify-between items-start mb-4">
          <div>
            <h3 className="text-lg font-bold text-gray-900">Risk & Compliance Review</h3>
            <p className="mt-1 text-sm text-gray-500">
              Review each criterion and select Pass or Fail
            </p>
          </div>
          {allPassed && allChecked ? (
            <div className="flex gap-2 items-center px-3 py-1.5 text-green-700 bg-green-50 rounded-lg border border-green-200">
              <CheckCircle2 className="w-4 h-4" />
              <span className="text-sm font-semibold">All Clear</span>
            </div>
          ) : anyFailed ? (
            <div className="flex gap-2 items-center px-3 py-1.5 text-red-700 bg-red-50 rounded-lg border border-red-200">
              <XCircle className="w-4 h-4" />
              <span className="text-sm font-semibold">Risk Found</span>
            </div>
          ) : (
            <div className="flex gap-2 items-center px-3 py-1.5 text-gray-700 bg-gray-50 rounded-lg border border-gray-200">
              <AlertTriangle className="w-4 h-4" />
              <span className="text-sm font-semibold">{checkedCount}/{checks.length} Reviewed</span>
            </div>
          )}
        </div>

        {/* Progress Bar */}
        <div className="space-y-2">
          <div className="flex justify-between items-center text-xs">
            <span className="font-medium text-gray-600">Progress</span>
            <span className="font-semibold text-gray-900">{checkedCount} of {checks.length} completed</span>
          </div>
          <div className="h-2 bg-gray-200 rounded-full overflow-hidden">
            <motion.div
              initial={{ width: 0 }}
              animate={{ width: `${progressPercentage}%` }}
              transition={{ duration: 0.5, ease: "easeOut" }}
              className={`h-full transition-colors ${
                allChecked && allPassed
                  ? "bg-green-600"
                  : allChecked && anyFailed
                  ? "bg-red-600"
                  : "bg-blue-600"
              }`}
            />
          </div>
        </div>
      </div>

      <div className="space-y-4">
        {checks.map((check, index) => {
          const Icon = check.icon;
          const currentStatus = riskChecks[check.id] ?? null;

          return (
            <motion.div
              key={check.id}
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: index * 0.05 }}
              className="p-5 bg-gray-50 rounded-xl border border-gray-200 hover:border-gray-300 transition-all"
            >
              <div className="flex gap-4 items-start mb-4">
                <div className="p-3 bg-white rounded-xl border border-gray-200 shadow-sm">
                  <Icon className="w-5 h-5 text-gray-700" />
                </div>
                <div className="flex-1">
                  <div className="flex gap-2 items-center mb-1">
                    <h4 className="font-semibold text-gray-900">{check.title}</h4>
                    {check.critical && (
                      <span className="px-2 py-0.5 text-xs font-medium text-red-700 bg-red-100 rounded-full">
                        Critical
                      </span>
                    )}
                  </div>
                  <p className="text-sm text-gray-600 leading-relaxed">{check.description}</p>
                </div>
              </div>

              {/* Action Buttons */}
              <div className="flex gap-2">
                <button
                  onClick={() => onRiskCheckChange(check.id, true)}
                  className={`flex-1 flex items-center justify-center gap-2 px-4 py-3 rounded-lg font-medium text-sm transition-all ${
                    currentStatus === true
                      ? "bg-green-600 text-white shadow-md scale-[1.02]"
                      : "bg-white text-gray-700 border border-gray-300 hover:border-green-300 hover:bg-green-50"
                  }`}
                >
                  <CheckCircle2 className={`w-4 h-4 ${currentStatus === true ? "text-white" : "text-green-600"}`} />
                  <span>{currentStatus === true ? "Verified ✓" : "Pass"}</span>
                </button>
                
                <button
                  onClick={() => onRiskCheckChange(check.id, false)}
                  className={`flex-1 flex items-center justify-center gap-2 px-4 py-3 rounded-lg font-medium text-sm transition-all ${
                    currentStatus === false
                      ? "bg-red-600 text-white shadow-md scale-[1.02]"
                      : "bg-white text-gray-700 border border-gray-300 hover:border-red-300 hover:bg-red-50"
                  }`}
                >
                  <XCircle className={`w-4 h-4 ${currentStatus === false ? "text-white" : "text-red-600"}`} />
                  <span>{currentStatus === false ? "Issue Found" : "Fail"}</span>
                </button>
              </div>

              {/* Status Indicator */}
              {currentStatus !== null && (
                <div className="flex gap-2 items-center justify-center mt-3 pt-3 border-t border-gray-200">
                  <span className="text-xs text-gray-500">Status:</span>
                  {currentStatus === true ? (
                    <span className="flex gap-1 items-center px-2 py-1 text-xs font-medium text-green-700 bg-green-100 rounded-full">
                      <CheckCircle2 className="w-3 h-3" />
                      No issues detected
                    </span>
                  ) : (
                    <span className="flex gap-1 items-center px-2 py-1 text-xs font-medium text-red-700 bg-red-100 rounded-full">
                      <AlertTriangle className="w-3 h-3" />
                      Requires attention
                    </span>
                  )}
                  <button
                    onClick={() => onRiskCheckChange(check.id, null)}
                    className="ml-auto text-xs text-gray-500 hover:text-gray-700 underline"
                  >
                    Reset
                  </button>
                </div>
              )}
            </motion.div>
          );
        })}
      </div>

      {anyFailed && (
        <div className="flex gap-3 items-start p-4 mt-4 bg-red-50 rounded-lg border-2 border-red-200">
          <AlertTriangle className="flex-shrink-0 w-5 h-5 text-red-600" />
          <div>
            <h4 className="mb-1 font-semibold text-red-900">Risk Detected</h4>
            <p className="text-sm text-red-800">
              One or more compliance issues have been identified. According to policy:
            </p>
            <ul className="mt-2 ml-4 text-sm text-red-800 list-disc">
              <li>Application should be <strong>rejected</strong>, OR</li>
              <li>Publisher should be placed on <strong>limited access with close monitoring</strong></li>
            </ul>
          </div>
        </div>
      )}

      {allPassed && allChecked && (
        <div className="flex gap-3 items-start p-4 mt-4 bg-green-50 rounded-lg border border-green-200">
          <CheckCircle2 className="flex-shrink-0 w-5 h-5 text-green-600" />
          <div>
            <h4 className="mb-1 font-semibold text-green-900">No Compliance Issues</h4>
            <p className="text-sm text-green-800">
              All risk and compliance checks have passed. This promoter shows no red flags.
            </p>
          </div>
        </div>
      )}
    </div>
  );
};

export default RiskComplianceReview;
